import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { AuthService } from '@app/core/services/auth.service';
import { RecaptchaService } from '@app/core/services/Recaptcha.service';
import { TokenService } from '@app/core/services/token.service';
import { UserAccountService } from '@app/core/services/user-account.service';
import { environment } from '@env/environment';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';


@Injectable({
  providedIn: 'root'
})
export class ChangePasswordModalService {
  private authService = inject(AuthService);
  private captchaService = inject(RecaptchaService);
  private http = inject(HttpClient);
  private tokenService = inject(TokenService);

  isOpen = signal(false);
  response = signal<boolean | null>(null);

  open(): Promise<boolean> {
    this.isOpen.set(true);
    this.response.set(null);

    return new Promise<boolean>((resolve) => {
      const interval = setInterval(() => {
        if (this.response() !== null) {
          clearInterval(interval);
          this.isOpen.set(false); // Cierra el modal automáticamente
          resolve(this.response()!);
        }
      }, 100);
    });
  }

  close() {
    this.response.set(false);
  }

  confirm() {
    this.response.set(true);
  }




  /**
     * Inicia el proceso de cambio de contraseña para un usuario.
     * @param email El correo electrónico del usuario.
     * @returns Un Observable con la respuesta del servidor.
     */
  startChangePassword(email: string): Observable<any> {
    // Validación opcional para asegurar que el email no esté vacío.
    if (!email) {
      return throwError(() => new Error('El correo electrónico es requerido.'));
    }

    // para que ya NO esperen ni validen un token de reCAPTCHA.
    // El nombre del endpoint podría cambiar, por ejemplo a '/auth/startChangePassword'.
    const url = `${environment.keyApi}/auth/startChangePasswordOnSession`;

    // El cuerpo de la petición ahora solo contiene el email.
    const body = { email }; // Atajo para { email: email }

    return this.http.post(url, body).pipe(
      catchError((error) => {
        console.error('Error en startChangePassword:', error);
        return throwError(() => error);
      })
    );
  }

  /**
    * Cambia la contraseña del usuario en la sesión activa.
    * @param data Objeto con la contraseña actual, nueva y (opcionalmente) código de verificación.
    * @returns Observable con la respuesta del servidor.
    */
  confirmChangePasswordOnSession(data: { password: string; newPassword: string; verificationCode: string }): Observable<any> {

    // ⚠️ Importante: El backend debe ser actualizado para que ya NO requiera un token de reCAPTCHA.
    const url = `${environment.keyApi}/auth/ChangePasswordOnSession`;

    // El cuerpo de la petición es simplemente el objeto 'data' que se pasa a la función.
    // Ya no se le añade el token.
    const body = data;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + this.tokenService.getToken()
    });


    return this.http.post(url, body, {headers}).pipe(
      catchError((error) => {
        console.error('Error en changePassword:', error);
        // Propagamos el error para que sea manejado por el código que llama a la función.
        return throwError(() => error);
      })
    );
  }









  // const body = {
  // "password": "edutin189823",
  // "newPassword": "fwefgfdfgdfgwe",
  // "otp": "123134",
  // "token": token
  // }



  /**
   * Initiates the first step of the password reset process for a user without an active session.
   */
  resetUserPasswordFirstStep(email: string): Observable<any> {
    return from(this.captchaService.getRecaptchaResponse()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No se pudo obtener el token de reCAPTCHA'));
        }

        const url = `${environment.keyApi}/auth/resetUserPassword`;
        const body = { email };
        const headers = new HttpHeaders({ 'X-Recaptcha-Token': token });

        // Si es número de celular, debe quedar: +573001234567@phone.edutin.com
        return this.http.post(url, body, { headers });
      }),
      catchError((error) => {
        console.error('Error en resetUserPasswordFirstStep:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Cambia la contraseña del usuario SIN la sesión activa.
   * @param data Objeto con la contraseña actual, nueva y código de verificación
   * @returns Observable con la respuesta del servidor
   */
  changePasswordNotAuth(data: { username: string, newPassword: string; verificationCode: string }): Observable<any> {
    return from(this.captchaService.getRecaptchaResponse()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No se pudo obtener el token de reCAPTCHA'));
        }

        const url = `${environment.keyApi}/auth/resetUserPasswordConfirm`;
        const body = { ...data };
        const headers = new HttpHeaders({ 'X-Recaptcha-Token': token });

        return this.http.post(url, body, { headers });
      }),
      catchError((error) => {
        console.error('Error en changePasswordNotAuth:', error);
        return throwError(() => error);
      })
    );
  }


  /**
    *Se ejecuta para enviar el código de verificación al nuevo correo 
    * @param data Objeto con la contraseña actual, nueva y código de verificación
    * @returns Observable con la respuesta del servidor
 */
  changeEmailFirst(data: { email: string }): Observable<any> {
    return from(this.captchaService.getRecaptchaResponse()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No se pudo obtener el token de reCAPTCHA'));
        }

        const url = `${environment.keyApi}/auth/changeEmail`;
        const body = { ...data };
        const headers = new HttpHeaders({
          'X-Recaptcha-Token': token,
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + this.tokenService.getToken()
        });

        return this.http.post(url, body, { headers });
      }),
      catchError((error) => {
        console.error('Error en changeEmailFirst:', error);
        return throwError(() => error);
      })
    );
  }


  confirmChangeEmail(data: { email: string; verificationCode: string }): Observable<any> {
    return from(this.captchaService.getRecaptchaResponse()).pipe(
      switchMap((token) => {
        if (!token) {
          return throwError(() => new Error('No se pudo obtener el token de reCAPTCHA'));
        }

        const url = `${environment.keyApi}/auth/changeEmailConfirm`;
        const body = { ...data };
        const headers = new HttpHeaders({
          'X-Recaptcha-Token': token,
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + this.tokenService.getToken()
        });

        return this.http.post(url, body, { headers });
      }),
      catchError((error) => {
        console.error('Error en confirmChangeEmail:', error);
        return throwError(() => error);
      })
    );
  }


}
